/**
 * POS sell screen — a SELF-CONTAINED, chrome-less surface.
 *
 * Deliberately decoupled from the admin shell (no sidebar / menu / admin
 * layout): it owns its full-viewport two-pane layout and talks ONLY to the
 * shell-agnostic ``/api/pos/*`` endpoints, so the SAME component mounts on the
 * admin ``/pos`` route AND on a standalone "POS endpoint display" page. Keep it
 * that way — no imports from the admin layout/menu here.
 *
 * NOTE (Phase A): this is a FUNCTIONAL scaffold for the design pass. It wires
 * every state — cash/comp/CARD (dual-rail reader + scan-to-pay QR) tender,
 * scan-to-add, sounds, the tip button — against the real endpoints + hooks, but
 * the visual design is intentionally plain. The design pass restyles it; the
 * implement pass assembles the designed components onto these same hooks.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Empty,
  Input,
  Modal,
  Popover,
  QRCode,
  Result,
  Segmented,
  Space,
  Spin,
  Typography,
} from "antd";

import {
  ApiError,
  api,
  type ArticleRow,
  type CardStatusResponse,
  type PosCatalogProduct,
  type PosCupConfig,
  type PosOrder,
  type PosStation,
  type PosTender,
  type CategoryRow,
  type ProductGroupRow,
  type RecipeRow,
} from "../../api/client";
import {
  Banknote,
  Bell,
  CreditCard,
  Gift,
  HandCoins,
  ScanBarcode,
  Ticket,
  Wine,
} from "lucide-react";

import { useBarcodeScanner } from "../../hooks/useBarcodeScanner";
import { posSound } from "../../lib/posSound";
import { FullscreenButton } from "./FullscreenButton";
import {
  eur,
  groupOrderLines,
  hhmm,
  lineLabel,
  lineSignedCents,
} from "./orderModel";
import { PosNumberPad } from "./PosNumberPad";
import { PosBadge } from "./posBrand";

/** Per-tender label + glyph for the recent-orders ticker + review modal. */
const TENDER_META: Record<PosTender, { label: string; Icon: typeof Banknote }> = {
  cash: { label: "Cash", Icon: Banknote },
  card: { label: "Card", Icon: CreditCard },
  voucher: { label: "Voucher", Icon: Ticket },
  staff_comp: { label: "Staff", Icon: Gift },
  client_comp: { label: "On the house", Icon: HandCoins },
};

/** The price the POS charges: the optional per-article POS price, else the
 * kiosk/catalog price. */
const posPrice = (a: ArticleRow): number => a.pos_price_cents ?? a.sell_price_cents;

// --- ad-hoc custom drink ----------------------------------------------------
type IngUnit = "liters" | "grams" | "count";
/** One picked ingredient of an ad-hoc drink: a consumable chain root + a
 * base-unit amount (mL for liters, g for grams, whole units for count). */
type CustomIngredient = {
  rootId: number;
  name: string;
  unit: IngUnit;
  amount: number;
};
/** A consumable's effective dimension: a saved override wins over the parsed
 * value; anything that isn't volumetric is treated as a whole-unit (count). */
const effUnit = (a: ArticleRow): IngUnit => {
  const u = a.unit_type_override ?? a.unit_type;
  return u === "liters" ? "liters" : u === "grams" ? "grams" : "count";
};
const ingStep = (u: IngUnit): number => (u === "liters" ? 10 : u === "grams" ? 5 : 1);
const ingDefault = (u: IngUnit): number =>
  u === "liters" ? 40 : u === "grams" ? 20 : 1;
const ingUnitLabel = (u: IngUnit): string =>
  u === "liters" ? "ml" : u === "grams" ? "g" : "×";
const ingUnitWord = (u: IngUnit): string =>
  u === "liters" ? "by ml" : u === "grams" ? "by g" : "by unit";

const newIdempotencyKey = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `pos-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

// Voucher code grammar (add-voucher-system): YYYY-PREFIX-NNNNNN, delimiter-
// agnostic so a QWERTZ tablet's "-" (→ "ß"/"/"/…) still matches. Mirrors the
// kiosk's user-scanner.js matcher; returns the canonical hyphenated code.
const VOUCHER_RE = /^(20\d\d)[^0-9A-Za-z]+([A-Za-z0-9]+)[^0-9A-Za-z]+(\d{6})$/;
const matchVoucher = (s: string): string | null => {
  const m = VOUCHER_RE.exec(s.trim());
  return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
};

// SumUp gives a reader checkout only ~60s to START on the device after arming;
// after that the Solo silently drops it while the QR rail keeps the intent
// awaiting (so nothing terminalises). The backend can't see that drop (a
// never-tapped checkout records no transaction), so once the reader has been
// waiting this long we flag it down on the client and offer a re-present. A small
// margin over 60s avoids crying wolf on a customer who taps right at the edge.
const READER_START_WINDOW_MS = 65_000;

/** A card payment running in the BACKGROUND for a parked order. */
type PendingCard = {
  order: PosOrder;
  status: "waiting" | "paid" | "declined" | "expired" | "error";
  qrUrl: string | null;
  readerArmed: boolean;
  // The reader rail died while the QR rail stays live (the tender keeps waiting):
  // a recorded decline / our 90s sweep, OR SumUp's silent ~60s device timeout.
  readerDown: boolean;
  // When the current reader arming began (epoch ms), for the ~60s timeout fallback;
  // null whenever no reader checkout is live. Reset on every (re-)arm.
  armedAt: number | null;
  receiptNo: string | null;
};

export const PosSellScreen = () => {
  const { message } = AntApp.useApp();

  const [stations, setStations] = useState<PosStation[] | null>(null);
  const [station, setStation] = useState<PosStation | null>(null);
  const [articles, setArticles] = useState<ArticleRow[] | null>(null);
  // barcode → product (for scan-to-add); loaded from the catalog bundle.
  const [byBarcode, setByBarcode] = useState<Map<string, PosCatalogProduct>>(
    new Map(),
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  // Venue cup/deposit config — gates the four quick-action buttons + lets the
  // cart name a cup line (a sale/return line of the configured cup article).
  const [cupConfig, setCupConfig] = useState<PosCupConfig | null>(null);
  // Recipes (servings + cocktails) → backs the custom-drink builder. Each one
  // is a sellable article that fans out its ingredients at settle, so adding a
  // serving is just adding that article's sale line.
  const [recipes, setRecipes] = useState<RecipeRow[]>([]);
  // Category ids flagged "hide from main POS" — their articles (filler
  // ingredients used only inside recipes) are kept off the catalog grid.
  const [hidePosCats, setHidePosCats] = useState<Set<number>>(new Set());
  // Catalog taxonomy for the grouped sell grid: categories + product groups,
  // each carrying a sort_order. The grid is laid out category-by-category (in
  // category order), and within a category the articles are ordered by their
  // product group (groups are sortable; the articles inside a group are not).
  const [categories, setCategories] = useState<CategoryRow[]>([]);
  const [productGroups, setProductGroups] = useState<ProductGroupRow[]>([]);
  const [builderOpen, setBuilderOpen] = useState(false);
  // The custom drink being assembled in the builder: a list of tapped servings
  // (each a recipe article). The drink's price = Σ of the servings' prices.
  const [drink, setDrink] = useState<
    { articleId: number; name: string; price: number }[]
  >([]);
  // Ad-hoc custom drink (add-pos-adhoc-custom-drink): a one-off drink built from
  // RAW ingredients (consumables + amounts) + a name + an operator-set price,
  // added as a single stockless line that fans out its ingredients at settle.
  const [customOpen, setCustomOpen] = useState(false);
  const [customName, setCustomName] = useState("");
  const [customPrice, setCustomPrice] = useState<number | null>(null);
  const [customIngs, setCustomIngs] = useState<CustomIngredient[]>([]);

  const [order, setOrder] = useState<PosOrder | null>(null);
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState("");
  // The manual barcode/voucher entry popover (the physical scanner feeds onScan
  // globally; this is the keyboard-less fallback behind the barcode button).
  const [scanOpen, setScanOpen] = useState(false);
  // The station's customer-display URL (token-signed), rendered as a QR in the
  // barcode popover so a second screen can be pointed at it to open the display
  // without fetching the link through the admin. Lazy-loaded on first open.
  const [displayUrl, setDisplayUrl] = useState<string | null>(null);
  // The scrolling order-history element — kept pinned to its newest line.
  const cartLinesRef = useRef<HTMLDivElement>(null);
  // Recent settled orders this shift — the bottom ticker, newest on the right;
  // tap a pill to review an order (amounts, payment) for a customer question.
  const [recent, setRecent] = useState<PosOrder[]>([]);
  const [recentDetail, setRecentDetail] = useState<PosOrder | null>(null);
  const [voided, setVoided] = useState<Set<number>>(new Set());
  // Dedupe by order id: a settled card can be recorded from more than one path
  // (the poll AND a paid-race recovery), and the ticker keys pills by id — a
  // duplicate push would collide React keys. Last write wins (freshest order).
  const pushRecent = useCallback(
    (o: PosOrder) =>
      setRecent((r) => [...r.filter((x) => x.id !== o.id), o].slice(-16)),
    [],
  );
  // How many tile columns currently fit the catalog (measured live so the
  // group packing adapts to the screen — a big group spreads across columns on
  // a tablet instead of running vertically).
  const [gridCols, setGridCols] = useState(4);

  const [tendered, setTendered] = useState<number | null>(null);
  // Which tender popup is open (cash keypad / comp confirm / voucher confirm).
  // `card` is handled by its own dual-rail overlay, never this modal.
  const [tenderModal, setTenderModal] = useState<PosTender | null>(null);
  const [settled, setSettled] = useState<PosOrder | null>(null);
  // A scanned voucher armed to pay the order: its code + spendable value (face
  // for a fresh voucher, live balance for a redeemed card).
  const [armedVoucher, setArmedVoucher] = useState<{
    code: string;
    available: number | null;
  } | null>(null);

  // Cart display mode (persisted): "list" rings every tap as its OWN line in
  // tap order (Pils, Snickers, Pils — no qty/steppers); "aggregated" merges
  // repeats into one line with a qty + −/+ steppers. Default = list.
  const [cartMode, setCartMode] = useState<"list" | "aggregated">(() => {
    try {
      return localStorage.getItem("pos.cartMode") === "aggregated"
        ? "aggregated"
        : "list";
    } catch {
      return "list";
    }
  });
  // In list mode a line has no steppers; tapping it reveals a red × to remove.
  const [selectedLineId, setSelectedLineId] = useState<number | null>(null);
  const changeCartMode = useCallback((m: "list" | "aggregated") => {
    setCartMode(m);
    setSelectedLineId(null);
    try {
      localStorage.setItem("pos.cartMode", m);
    } catch {
      /* ignore unavailable storage */
    }
  }, []);

  // --- initial load: stations + active catalog + barcode map ---------------
  useEffect(() => {
    let alive = true;
    Promise.all([
      api.posListStations(),
      api.listArticles({ active: true }),
      api.posCatalogSync().catch(() => null), // scan map is best-effort
      api.posGetCupConfig().catch(() => null), // cup/deposit buttons are best-effort
      api.listRecipes().catch(() => [] as RecipeRow[]), // custom-drink builder is best-effort
      api.listCategories().catch(() => [] as CategoryRow[]), // hide-from-POS + grid order
      api.listProductGroups().catch(() => [] as ProductGroupRow[]), // grid sub-order
    ])
      .then(([st, arts, cat, cup, recs, cats, groups]) => {
        if (!alive) return;
        setStations(st);
        if (st.length === 1) setStation(st[0]);
        setArticles(arts.filter((a) => a.active));
        setCupConfig(cup);
        setRecipes(recs);
        setHidePosCats(
          new Set(cats.filter((c) => c.hide_from_pos).map((c) => c.id)),
        );
        setCategories(cats);
        setProductGroups(groups);
        if (cat) {
          const m = new Map<string, PosCatalogProduct>();
          for (const p of cat.products)
            for (const b of p.barcodes) m.set(b, p);
          setByBarcode(m);
        }
      })
      .catch((e) => {
        if (!alive) return;
        setLoadError(
          e instanceof ApiError ? e.detail : "Failed to load POS data",
        );
      });
    return () => {
      alive = false;
    };
  }, []);

  const reportError = useCallback(
    (e: unknown) => {
      message.error(e instanceof ApiError ? e.detail : "Something went wrong");
    },
    [message],
  );

  // STORNO a recent order — reverse the whole sale (a card payment is refunded
  // back to the card; cash/comp is voided). The idempotency key is STABLE per
  // order, so a lost-response retry hits the backend's idempotent re-drive (and
  // re-attempts the card refund) instead of a 409 that would strand the refund.
  const stornoOrder = useCallback(
    async (o: PosOrder) => {
      setBusy(true);
      try {
        const res = await api.posCorrectOrder(o.id, {
          client_idempotency_key: `storno-${o.id}`,
        });
        posSound.play("storno");
        setVoided((v) => new Set(v).add(o.id));
        setRecentDetail(null);
        // For a card order, anything other than a clean 'issued' means the money
        // did NOT go back to the card — make the operator refund it by hand.
        if (
          o.tender === "card" &&
          res.card_refund != null &&
          res.card_refund !== "issued"
        )
          message.warning(
            `Card refund ${res.card_refund.replace("_", " ")} — refund the card manually.`,
          );
      } catch (e) {
        reportError(e);
      } finally {
        setBusy(false);
      }
    },
    [message, reportError],
  );

  // Add a sale line for an article id at a price — lazily creating the order.
  const addByArticle = useCallback(
    async (articleId: number, priceCents: number) => {
      if (!station || busy) return;
      setBusy(true);
      try {
        let current = order;
        if (current === null) {
          current = await api.posCreateOrder(station.id, newIdempotencyKey());
        }
        // The order is always a FLOW of individual lines (one per tap, newest
        // last) — the single source of truth. List view shows that flow as-is;
        // Grouped view aggregates it by article at render time. So a tap always
        // adds a fresh line, regardless of the current view.
        const next = await api.posAddLine(current.id, {
          article_id: articleId,
          qty: 1,
          unit_price_cents: priceCents,
        });
        setOrder(next);
      } catch (e) {
        reportError(e);
      } finally {
        setBusy(false);
      }
    },
    [station, busy, order, reportError],
  );

  const addArticle = useCallback(
    (art: ArticleRow) => addByArticle(art.id, posPrice(art)),
    [addByArticle],
  );

  // Scan-to-add: resolve a barcode → product; ok/err cue either way.
  // A scanned voucher → preview its spendable value + arm it as the voucher
  // tender (the order is charged to the voucher holder at settle).
  const armVoucher = useCallback(
    async (code: string) => {
      try {
        const pv = await api.posVoucherPreview(code);
        if (pv.status === "not_found" || pv.status === "disabled") {
          posSound.play("scanErr");
          message.warning(
            pv.status === "disabled" ? "Voucher is disabled" : "Voucher not found",
          );
          return;
        }
        // A redeemed card whose live balance couldn't be read: don't arm with a
        // success cue (it would dead-end on a misleading "does not cover") —
        // treat it as a scan error so the operator re-scans.
        if (pv.available_cents == null) {
          posSound.play("scanErr");
          message.warning("Could not read the voucher balance — re-scan");
          return;
        }
        posSound.play("scanOk");
        setArmedVoucher({ code, available: pv.available_cents });
        // Pop the voucher confirmation straight away (it shows coverage vs the
        // order total + a Settle confirm).
        setTenderModal("voucher");
      } catch (e) {
        reportError(e);
      }
    },
    [message, reportError],
  );

  const onScan = useCallback(
    (code: string) => {
      const c = code.trim();
      if (!c) return;
      // A voucher code (delimiter-agnostic) is detected BEFORE the product
      // barcode lookup — it pays the order, it isn't an article.
      const voucherCode = matchVoucher(c);
      if (voucherCode) {
        setScan("");
        void armVoucher(voucherCode);
        return;
      }
      const p = byBarcode.get(c);
      if (p) {
        posSound.play("scanOk");
        void addByArticle(p.article_id, p.pos_price_cents);
      } else {
        posSound.play("scanErr");
        message.warning(`Unknown barcode: ${c}`);
      }
      setScan("");
    },
    [byBarcode, addByArticle, message, armVoucher],
  );

  // Hands-free physical scanner: a global document listener feeds onScan, so the
  // operator just scans an article barcode or a voucher — no field to focus
  // (the heuristic ignores keystrokes while a form input is focused). Manual
  // entry lives behind the barcode button's popover.
  useBarcodeScanner(onScan);

  // Keep the order history pinned to its newest line as items are rung in.
  useEffect(() => {
    const el = cartLinesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [order?.lines.length]);

  const changeQty = useCallback(
    async (lineId: number, qty: number) => {
      if (!order || busy) return;
      setBusy(true);
      try {
        const next =
          qty <= 0
            ? await api.posDeleteLine(order.id, lineId)
            : await api.posUpdateLine(order.id, lineId, qty);
        if (qty <= 0) posSound.play("storno");
        setOrder(next);
      } catch (e) {
        reportError(e);
      } finally {
        setBusy(false);
      }
    },
    [order, busy, reportError],
  );

  const clearCart = useCallback(async () => {
    if (!order || busy) return;
    setBusy(true);
    try {
      await api.posDiscardOrder(order.id);
      posSound.play("storno");
      setOrder(null);
      setTendered(null);
      setArmedVoucher(null);
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  }, [order, busy, reportError]);

  // Cup / on-site-deposit quick-actions (one cup, one deposit → no picker).
  // OUT = leaves the bar = a charge; IN = comes back = a refund. Lazily create
  // the order (like addByArticle), then add the line server-side.
  const runQuickAction = useCallback(
    async (kind: "cup" | "deposit", direction: "out" | "in") => {
      if (!station || busy) return;
      setBusy(true);
      try {
        let current = order;
        if (current === null) {
          current = await api.posCreateOrder(station.id, newIdempotencyKey());
        }
        const next =
          kind === "cup"
            ? await api.posCupAction(current.id, direction)
            : await api.posDepositAction(current.id, direction);
        // No sound on cup / pfand quick-actions (per request).
        setOrder(next);
      } catch (e) {
        reportError(e);
      } finally {
        setBusy(false);
      }
    },
    [station, busy, order, reportError],
  );

  // Mirror the backend's _cup_config: a cup is "configured" only when BOTH the
  // article AND the deposit price are set (else cup_action 422s).
  const cupReady =
    cupConfig?.cup_article_id != null && cupConfig?.cup_price_cents != null;
  // Bottle pfand is usable only when configured AND the venue kill-switch is on.
  const depositEnabled = cupConfig?.deposit_enabled !== false;
  const depositReady =
    cupConfig?.deposit_pfand_value_id != null && depositEnabled;
  // Card flow: background (park + free the till + pill) vs foreground/blocking.
  const cardBackgroundEnabled = cupConfig?.card_background_enabled !== false;

  // article id → name, so a cart sale line shows the article name (not "Art #id").
  const nameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const a of articles ?? []) m.set(a.id, a.name);
    for (const p of byBarcode.values()) m.set(p.article_id, p.name);
    return m;
  }, [articles, byBarcode]);

  // Recipe servings sellable in the builder: resolve each recipe's chain root to
  // a concrete article (id + POS price). A recipe that maps to no active article
  // (archived / leaf-only) is dropped — it can't be tapped to a sale line.
  const recipeServings = useMemo(() => {
    const byRoot = new Map<number, ArticleRow>();
    for (const a of articles ?? []) {
      if (!byRoot.has(a.chain_root_id) || a.is_chain_root)
        byRoot.set(a.chain_root_id, a);
    }
    return recipes
      .map((r) => {
        const art = byRoot.get(r.article_chain_root_id);
        if (!art) return null;
        return {
          recipeId: r.id,
          articleId: art.id,
          name: art.name,
          price: posPrice(art),
          available: r.available,
          cogsCents: r.cogs_cents,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [recipes, articles]);

  // Article ids that are recipes (a sellable drink that depletes ingredients,
  // not its own stock) — used to mark them apart from plain articles in the grid.
  const recipeArticleIds = useMemo(
    () => new Set(recipeServings.map((s) => s.articleId)),
    [recipeServings],
  );

  const drinkTotal = useMemo(
    () => drink.reduce((sum, s) => sum + s.price, 0),
    [drink],
  );

  // Recipe chain roots — excluded from the ad-hoc ingredient picker (a recipe
  // cannot itself be a custom-drink ingredient: no nesting, enforced server-side).
  const recipeRoots = useMemo(
    () => new Set(recipes.map((r) => r.article_chain_root_id)),
    [recipes],
  );
  // The ad-hoc ingredient picker: one entry per consumable chain root (a real
  // stocked article), name + effective dimension, recipes excluded. Sorted A→Z.
  const consumables = useMemo(() => {
    const byRoot = new Map<number, { rootId: number; name: string; unit: IngUnit }>();
    for (const a of articles ?? []) {
      if (!a.active || recipeRoots.has(a.chain_root_id)) continue;
      const cur = byRoot.get(a.chain_root_id);
      if (!cur || a.is_chain_root)
        byRoot.set(a.chain_root_id, {
          rootId: a.chain_root_id,
          name: a.name,
          unit: effUnit(a),
        });
    }
    return [...byRoot.values()].sort((x, y) => x.name.localeCompare(y.name));
  }, [articles, recipeRoots]);

  const bumpIng = useCallback((idx: number, delta: number) => {
    setCustomIngs((xs) =>
      xs.map((i, j) =>
        j === idx ? { ...i, amount: Math.max(1, i.amount + delta) } : i,
      ),
    );
  }, []);

  // Commit the ad-hoc drink: ONE stockless line carrying its ingredients (the
  // backend fans them out at settle). The name defaults if the operator skipped it.
  const commitCustomDrink = useCallback(async () => {
    if (!station || customIngs.length === 0 || customPrice == null || busy)
      return;
    setBusy(true);
    try {
      const current =
        order ?? (await api.posCreateOrder(station.id, newIdempotencyKey()));
      const updated = await api.posAddCustomDrink(current.id, {
        name: customName.trim() || "Custom drink",
        unit_price_cents: customPrice,
        ingredients: customIngs.map((i) => ({
          consumable_chain_root_id: i.rootId,
          amount: i.amount,
          unit_type: i.unit,
        })),
      });
      setOrder(updated);
      setCustomOpen(false);
      setCustomName("");
      setCustomPrice(null);
      setCustomIngs([]);
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  }, [order, station, customIngs, customPrice, customName, busy, reportError]);

  // Commit the assembled drink: add each tapped serving as a sale line (the
  // backend fans out its ingredients at settle). We MUST NOT loop over the
  // order-closure-bound addByArticle — its captured `order` never advances
  // between awaits, so a fresh cart would spawn a new draft order per serving
  // (only the last surviving). Instead: aggregate repeated servings into
  // per-article quantities, resolve/create the order ONCE, and thread the
  // running order object forward through each add.
  const commitDrink = useCallback(async () => {
    if (drink.length === 0 || !station || busy) return;
    setBusy(true);
    try {
      const byArticle = new Map<number, { price: number; qty: number }>();
      for (const s of drink) {
        const prev = byArticle.get(s.articleId);
        byArticle.set(s.articleId, {
          price: s.price,
          qty: (prev?.qty ?? 0) + 1,
        });
      }
      let current =
        order ?? (await api.posCreateOrder(station.id, newIdempotencyKey()));
      for (const [articleId, { price, qty }] of byArticle) {
        // Dedup against the THREADED order's lines (excluding auto-bundled
        // children), so a serving already in the cart increments instead of
        // duplicating.
        const existing = current.lines.find(
          (l) =>
            l.kind === "sale" &&
            l.article_id === articleId &&
            l.parent_line_id == null,
        );
        current = existing
          ? await api.posUpdateLine(current.id, existing.id, existing.qty + qty)
          : await api.posAddLine(current.id, {
              article_id: articleId,
              qty,
              unit_price_cents: price,
            });
      }
      setOrder(current);
      setDrink([]);
      setBuilderOpen(false);
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  }, [drink, station, busy, order, reportError]);

  const total = order?.total_cents ?? 0;
  const change = tendered !== null ? tendered - total : null;
  // Vouchers (like comps) DROP the on-site deposit — they cover the drinks, not
  // the refundable deposit (matches the backend voucher settle, which strips
  // deposit lines). So coverage + the confirm total use this deposit-excluded
  // figure; cups are SALE lines and stay charged.
  const depositCents = (order?.lines ?? [])
    .filter((l) => l.kind === "deposit")
    .reduce((s, l) => s + l.unit_price_cents * l.qty, 0);
  const voucherTotal = total - depositCents;
  // A voucher must cover the WHOLE positive (deposit-excluded) order total.
  const voucherCovers =
    armedVoucher != null &&
    armedVoucher.available != null &&
    voucherTotal > 0 &&
    armedVoucher.available >= voucherTotal;
  // The tender buttons are live once the cart has something to settle.
  const canPay = !!order && order.lines.length > 0 && !busy;
  // Cash needs a tendered amount only when money is OWED; a refund/swap order
  // (total ≤ 0, e.g. a pure Cup-in) pays cash OUT and settles with no tender —
  // matching the backend (settle_order requires tendered_cents only when total>0).
  const cashCovers =
    total <= 0 || (tendered !== null && tendered >= total);

  // Grouped order lines for the tender popups: `all` (qty × name, drinks +
  // deposits) for the cash view; `drinks` (sale lines only) for the comp confirm
  // (deposits are dropped on the house).
  const orderGrouped = useMemo(() => {
    const cupId = cupConfig?.cup_article_id ?? null;
    const lines = order?.lines ?? [];
    return {
      all: groupOrderLines(lines, cupId, nameById),
      drinks: groupOrderLines(
        lines,
        cupId,
        nameById,
        (l) => l.kind === "sale" && !(cupId != null && l.article_id === cupId),
      ),
    };
  }, [order, cupConfig, nameById]);

  // The GROUPED cart view: a pure client-side aggregation of the individual-line
  // flow, by article (Cup/Bottle/return grouped by label). Drinks are steppable:
  // − removes the FRESHEST line of that article (LIFO; its bundled children
  // cascade server-side), + adds a fresh line at the end.
  const cartGroups = useMemo(() => {
    const cupId = cupConfig?.cup_article_id ?? null;
    type G = {
      key: string;
      label: string;
      qty: number;
      cents: number;
      freshestId: number;
      freshestQty: number;
      articleId: number | null;
      priceCents: number;
      steppable: boolean;
      refund: boolean;
    };
    const m = new Map<string, G>();
    for (const l of order?.lines ?? []) {
      const isCup = cupId != null && l.article_id === cupId;
      // An ad-hoc custom drink (NULL article + custom name) is its OWN line —
      // never aggregated (each is a distinct one-off) and not stepper-able (it
      // has no article to re-add); keyed by line id.
      const isAdHoc = l.kind === "sale" && l.custom_name != null;
      const isDrink = l.kind === "sale" && !isCup && !isAdHoc;
      const label = lineLabel(l, cupId, nameById);
      const key = isAdHoc
        ? `c:${l.id}`
        : isDrink
          ? `a:${l.article_id}`
          : `${l.kind}:${label}`;
      const g = m.get(key) ?? {
        key,
        label,
        qty: 0,
        cents: 0,
        freshestId: l.id,
        freshestQty: l.qty,
        articleId: l.article_id ?? null,
        priceCents: l.unit_price_cents,
        steppable: isDrink,
        refund: false,
      };
      g.qty += l.qty;
      g.cents += lineSignedCents(l);
      if (l.id >= g.freshestId) {
        g.freshestId = l.id;
        g.freshestQty = l.qty;
      }
      m.set(key, g);
    }
    return [...m.values()].map((g) => ({ ...g, refund: g.cents < 0 }));
  }, [order, cupConfig, nameById]);

  // --- card tender: BACKGROUNDED ------------------------------------------
  // Arming a card PARKS the order and frees the till for a new sale; the parked
  // card is polled in the background (a blinking pill), turns green + plays the
  // strichliste card sound on paid, and pops a LOCKED failure view (settle in
  // cash / cancel) on decline/expire/error. ONE pending card at a time.
  const [pending, setPending] = useState<PendingCard | null>(null);
  // A synchronous mirror so the background poll (whose effect deps deliberately
  // exclude the changing `pending` object) can read the parked order to
  // synthesize a recent pill when a settled-order re-fetch fails.
  const pendingRef = useRef<PendingCard | null>(null);
  pendingRef.current = pending;
  const [cardBusyOpen, setCardBusyOpen] = useState(false);
  const [fallbackCash, setFallbackCash] = useState(false); // failure → cash pad
  const [fallbackTendered, setFallbackTendered] = useState<number | null>(null);

  const startCardPayment = useCallback(async () => {
    if (!order || order.lines.length === 0 || busy || total < 0) return;
    if (pending) {
      // A card is still parked — waiting, briefly green-paid, or a locked
      // failure. Never silently overwrite it. Surface the manage popup for a
      // waiting card; a paid pill clears itself in a moment, a failed one keeps
      // its own locked modal up.
      if (pending.status === "waiting") setCardBusyOpen(true);
      return;
    }
    const parked = order;
    setBusy(true);
    try {
      const r = await api.posPayCard(parked.id);
      setPending({
        order: parked,
        status: "waiting",
        qrUrl: r.online_checkout_url,
        readerArmed: r.reader_armed,
        readerDown: false,
        armedAt: r.reader_armed ? Date.now() : null,
        receiptNo: null,
      });
      // Free the till for the next order while the card runs in the background.
      setOrder(null);
      setTendered(null);
      setArmedVoucher(null);
      // Foreground card mode (backgrounding off): open the BLOCKING manage modal
      // immediately so the operator stays on this payment until it resolves —
      // no background pill, no starting a new sale mid-flight.
      if (!cardBackgroundEnabled) setCardBusyOpen(true);
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  }, [order, busy, total, pending, cardBackgroundEnabled, reportError]);

  // Background poll of the parked card while it's waiting.
  const pendingId = pending?.order.id ?? null;
  const pendingWaiting = pending?.status === "waiting";
  const cardFailed =
    pending != null &&
    (pending.status === "declined" ||
      pending.status === "expired" ||
      pending.status === "error");
  useEffect(() => {
    if (pendingId == null || !pendingWaiting) return;
    let alive = true;
    let timer: ReturnType<typeof setTimeout>;
    const poll = async () => {
      try {
        const s = await api.posCardStatus(pendingId);
        if (!alive) return;
        if (s.order_status === "settled") {
          posSound.play("cardOk");
          // A confirmed-paid sale must never vanish: retry the fetch once, and
          // if it still fails synthesize the recent pill from the parked order
          // (status/tender/receipt are known) so the ticker always records it.
          let o = await api.posGetOrder(pendingId).catch(() => null);
          if (!o) o = await api.posGetOrder(pendingId).catch(() => null);
          if (!alive) return;
          const parked = pendingRef.current?.order;
          if (o) pushRecent(o);
          else if (parked && parked.id === pendingId)
            pushRecent({
              ...parked,
              status: "settled",
              tender: "card",
              receipt_no: s.receipt_no,
            });
          setPending((p) =>
            p && p.order.id === pendingId
              ? { ...p, status: "paid", receiptNo: s.receipt_no }
              : p,
          );
          return;
        }
        const st = s.intent_status;
        if (st === "failed" || st === "cancelled") {
          posSound.play("cardErr");
          setPending((p) => (p ? { ...p, status: "declined" } : null));
          return;
        }
        if (st === "expired") {
          posSound.play("cardErr");
          setPending((p) => (p ? { ...p, status: "expired" } : null));
          return;
        }
        // Still awaiting (QR rail alive). Flag a dead reader so the manage view can
        // offer a re-present: the backend sees a recorded decline / our 90s sweep
        // (reader_failed); SumUp's own ~60s "time to start" timeout is invisible
        // there (leg stays pending) so also flag it once an armed reader has been
        // waiting past the window.
        const armedAt = pendingRef.current?.armedAt ?? null;
        const timedOut =
          s.reader_armed &&
          armedAt != null &&
          Date.now() - armedAt > READER_START_WINDOW_MS;
        const down = s.reader_failed || timedOut;
        setPending((p) =>
          p && p.order.id === pendingId
            ? { ...p, readerArmed: s.reader_armed, readerDown: down }
            : p,
        );
        timer = setTimeout(() => void poll(), 1500);
      } catch {
        if (alive) timer = setTimeout(() => void poll(), 1500);
      }
    };
    timer = setTimeout(() => void poll(), 1500);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [pendingId, pendingWaiting, pushRecent]);

  // A paid card lingers green briefly, then the pill clears.
  useEffect(() => {
    if (pending?.status !== "paid") return;
    const t = setTimeout(
      () => setPending((p) => (p?.status === "paid" ? null : p)),
      6000,
    );
    return () => clearTimeout(t);
  }, [pending?.status]);

  // Cancelling a live reader leg can RACE a tap: the backend is paid-race-safe
  // and, rather than cancel, SETTLES the order on the card and returns
  // order_status 'settled'. If that happened, record the paid sale (real fetch,
  // else synthesized) so it lands in the ticker + is storno-able, clear the
  // pending, and tell the caller to SKIP its cash-settle / discard — otherwise
  // we'd 409 and either invite a physical-cash double-charge or silently drop a
  // card the customer actually paid. Returns true when the card already paid.
  const captureIfCardPaid = useCallback(
    async (parked: PosOrder, cancelRes: CardStatusResponse | undefined) => {
      if (cancelRes?.order_status !== "settled") return false;
      const fetched = await api.posGetOrder(parked.id).catch(() => null);
      posSound.play("cardOk");
      pushRecent(
        fetched ?? {
          ...parked,
          status: "settled",
          tender: "card",
          receipt_no: cancelRes.receipt_no,
        },
      );
      setPending(null);
      setFallbackCash(false);
      setFallbackTendered(null);
      return true;
    },
    [pushRecent],
  );

  // Cancel a parked card — release the reader + void the order (it is NOT
  // brought back to the till). If the card paid during the cancel, KEEP the
  // settled order instead of discarding it.
  const cancelPending = useCallback(async () => {
    if (!pending) return;
    const parked = pending.order;
    setBusy(true);
    try {
      let cancelRes: CardStatusResponse | undefined;
      try {
        cancelRes = await api.posCancelCard(parked.id);
      } catch {
        /* 404 = nothing live / intent already terminal — fine to discard */
      }
      if (await captureIfCardPaid(parked, cancelRes)) return; // card paid → kept
      await api.posDiscardOrder(parked.id).catch(() => undefined);
      setPending(null);
      setFallbackCash(false);
      setFallbackTendered(null);
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  }, [pending, captureIfCardPaid, reportError]);

  // Re-present the parked card's amount to the physical reader after a reader
  // timeout/decline — a FRESH reader checkout on the SAME live intent (the QR rail
  // and the order are untouched). Paid-race-safe: if a tap paid in the same instant
  // the backend SETTLES and captureIfCardPaid keeps it instead of re-arming over.
  const rearmReader = useCallback(async () => {
    if (!pending || pending.status !== "waiting") return;
    const parked = pending.order;
    setBusy(true);
    try {
      const res = await api.posRearmReader(parked.id);
      if (await captureIfCardPaid(parked, res)) return; // paid mid-call → kept
      setPending((p) =>
        p && p.order.id === parked.id
          ? {
              ...p,
              readerArmed: res.reader_armed,
              readerDown: false,
              armedAt: res.reader_armed ? Date.now() : null, // restart the ~60s window
            }
          : p,
      );
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  }, [pending, captureIfCardPaid, reportError]);

  // Settle a parked card AS CASH instead. If the card paid during the reader
  // release, take THAT (no cash double-charge); also recover from a 409 if the
  // order settles on the card after a non-settled cancel but before our settle.
  const settleFallbackCash = useCallback(async () => {
    if (!pending) return;
    const parked = pending.order;
    const oTotal = parked.total_cents;
    setBusy(true);
    try {
      let cancelRes: CardStatusResponse | undefined;
      try {
        cancelRes = await api.posCancelCard(parked.id); // release any reader leg
      } catch {
        /* 404 = nothing live — fine to settle as cash */
      }
      if (await captureIfCardPaid(parked, cancelRes)) return; // card paid → no cash
      const done = await api.posSettle(parked.id, {
        tender: "cash",
        tendered_cents: oTotal > 0 ? (fallbackTendered ?? oTotal) : undefined,
      });
      posSound.play("cashPayment");
      pushRecent(done);
      setPending(null);
      setFallbackCash(false);
      setFallbackTendered(null);
    } catch (e) {
      // The order may have settled on the card out from under us (a paid race
      // after a non-settled cancel) → posSettle 409s 'not a draft'. Recover it
      // as a paid card sale rather than surfacing an error that invites a
      // re-charge.
      const recovered = await api.posGetOrder(parked.id).catch(() => null);
      if (recovered?.status === "settled") {
        posSound.play("cardOk");
        pushRecent(recovered);
        setPending(null);
        setFallbackCash(false);
        setFallbackTendered(null);
      } else {
        reportError(e);
      }
    } finally {
      setBusy(false);
    }
  }, [pending, fallbackTendered, pushRecent, captureIfCardPaid, reportError]);

  // Settle with an EXPLICIT tender (the popup that triggers it owns the tender,
  // so we never read the possibly-stale `tender` state mid-dispatch).
  const doSettle = useCallback(
    async (t: PosTender) => {
      if (!order || order.lines.length === 0) return;
      setBusy(true);
      try {
        // Comps go on the house — the backend's comp settle only accepts SALE
        // lines, and a comped drink isn't charged a deposit anyway. Drop the
        // deposit CHARGES: a bottle-out (deposit, qty>0) and a cup-out (the cup
        // article sold). KEEP refund/return lines (a bottle/cup-in is money owed
        // back to the guest — never silently dropped; the backend will reject a
        // comp that still carries one, surfacing it to the operator). Refresh
        // state + skip already-gone lines per delete, so a mid-loop failure is
        // resumable instead of wedging the order half-stripped.
        if (t === "staff_comp" || t === "client_comp") {
          const cupId = cupConfig?.cup_article_id ?? null;
          let cur = order;
          const toDrop = cur.lines.filter(
            (x) =>
              (x.kind === "deposit" && x.qty > 0) ||
              (cupId != null && x.article_id === cupId && x.kind === "sale"),
          );
          for (const l of toDrop) {
            if (!cur.lines.some((x) => x.id === l.id)) continue;
            cur = await api.posDeleteLine(cur.id, l.id);
            setOrder(cur);
          }
        }
        const done = await api.posSettle(order.id, {
          tender: t,
          // Only when money is OWED — a refund / deposit-return (total ≤ 0) pays
          // cash OUT and settles with no amount received.
          tendered_cents:
            t === "cash" && total > 0 ? (tendered ?? total) : undefined,
          voucher_code:
            t === "voucher" ? (armedVoucher?.code ?? undefined) : undefined,
        });
        posSound.play("cashPayment");
        setSettled(done);
        pushRecent(done);
        setOrder(null);
        setTendered(null);
        setArmedVoucher(null);
        setTenderModal(null);
      } catch (e) {
        reportError(e);
      } finally {
        setBusy(false);
      }
    },
    [order, tendered, total, armedVoucher, reportError, pushRecent, cupConfig],
  );

  // A tender button: Card arms + backgrounds the payment (frees the till); the
  // rest open a popup (cash keypad, comp/voucher confirmation).
  const openTender = useCallback(
    (t: PosTender) => {
      if (!order || order.lines.length === 0 || busy) return;
      if (t === "card") {
        void startCardPayment();
        return;
      }
      if (t === "cash") setTendered(null);
      // Flip the customer display to the grouped "settling" view (card
      // self-signals via its intent, so only the popup tenders need this).
      void api.posPresent(order.id, t).catch(() => undefined);
      setTenderModal(t);
    },
    [order, busy, startCardPayment],
  );

  // POS-visible articles (drop the "hide from main POS" filler-ingredient
  // categories) — the basis for both the grid and the category rail.
  const posArticles = useMemo(
    () =>
      (articles ?? []).filter(
        (a) => a.category_id == null || !hidePosCats.has(a.category_id),
      ),
    [articles, hidePosCats],
  );

  // The grouped sell grid: categories in sort order, and within each category
  // the articles bucketed by product group (groups in sort order; an unnamed
  // "ungrouped" bucket sorts last). Articles inside a group keep catalog order
  // (not sortable). Uncategorised articles fall into a trailing "Other" section.
  const catalogSections = useMemo(() => {
    const groupSort = new Map(productGroups.map((g) => [g.id, g.sort_order]));
    const groupName = new Map(productGroups.map((g) => [g.id, g.name]));

    const byCat = new Map<number | null, ArticleRow[]>();
    for (const a of posArticles) {
      const k = a.category_id ?? null;
      const bucket = byCat.get(k);
      if (bucket) bucket.push(a);
      else byCat.set(k, [a]);
    }

    const buildGroups = (arts: ArticleRow[]) => {
      const byGroup = new Map<number | null, ArticleRow[]>();
      for (const a of arts) {
        const g = a.product_group_id ?? null;
        const bucket = byGroup.get(g);
        if (bucket) bucket.push(a);
        else byGroup.set(g, [a]);
      }
      return [...byGroup.entries()]
        .map(([gid, articles]) => ({
          groupId: gid,
          groupName:
            gid != null
              ? (groupName.get(gid) ?? articles[0]?.product_group_name ?? null)
              : null,
          articles,
        }))
        .sort((x, y) => {
          // ungrouped (null) sorts after every real group.
          const sx = x.groupId != null ? (groupSort.get(x.groupId) ?? 1e8) : 1e9;
          const sy = y.groupId != null ? (groupSort.get(y.groupId) ?? 1e8) : 1e9;
          return sx - sy;
        });
    };

    const sections: {
      catId: number | null;
      catName: string;
      groups: ReturnType<typeof buildGroups>;
    }[] = [];
    for (const c of [...categories].sort((a, b) => a.sort_order - b.sort_order)) {
      const arts = byCat.get(c.id);
      if (arts && arts.length)
        sections.push({ catId: c.id, catName: c.name, groups: buildGroups(arts) });
    }
    const other = byCat.get(null);
    if (other && other.length)
      sections.push({ catId: null, catName: "Other", groups: buildGroups(other) });
    return sections;
  }, [posArticles, categories, productGroups]);

  // Live column count for the grouped grid — a callback ref + ResizeObserver on
  // the catalog body, so group spans stay within what actually fits.
  const POS_TILE_W = 156; // target tile width (px)
  const POS_TILE_GAP = 12;
  const sectionsRef = useRef<ResizeObserver | null>(null);
  const measureCatalog = useCallback((el: HTMLDivElement | null) => {
    sectionsRef.current?.disconnect();
    if (!el) return;
    const compute = () => {
      const w = el.clientWidth;
      setGridCols(
        Math.max(1, Math.floor((w + POS_TILE_GAP) / (POS_TILE_W + POS_TILE_GAP))),
      );
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    sectionsRef.current = ro;
  }, []);

  // --- render states ------------------------------------------------------
  if (loadError) {
    return (
      <Shell>
        <Alert type="error" showIcon message="POS unavailable" description={loadError} />
      </Shell>
    );
  }
  if (stations === null || articles === null) {
    return (
      <Shell>
        <Spin size="large" />
      </Shell>
    );
  }
  if (settled) {
    // Itemised summary — group the settled lines by article (qty × name) and
    // sum, regardless of whether the cart was rung up in list or grouped mode,
    // so the finalization always shows the normal aggregated total of things.
    const settledCupId = cupConfig?.cup_article_id ?? null;
    const summary = groupOrderLines(settled.lines, settledCupId, nameById);
    return (
      <Shell glow>
        <Card className="pos-receipt-card" style={{ width: 420 }}>
          <Result
            status="success"
            title="Sale complete"
            subTitle={
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <span>Receipt {settled.receipt_no}</span>
                <div className="pos-receipt-lines">
                  {summary.map((g, i) => (
                    <div key={i} className="pos-receipt-line">
                      <span>
                        {g.qty} × {g.label}
                      </span>
                      <span>{eur(g.cents)}</span>
                    </div>
                  ))}
                </div>
                {/* Quick-check table for handing out change: total, received,
                    and the change in green, values right-aligned. */}
                <div className="pos-receipt-summary">
                  <div className="pos-receipt-summary__row">
                    <span>Total</span>
                    <span>{eur(settled.total_cents)}</span>
                  </div>
                  {settled.tender === "cash" &&
                    settled.tendered_cents != null && (
                      <div className="pos-receipt-summary__row">
                        <span>Received</span>
                        <span>{eur(settled.tendered_cents)}</span>
                      </div>
                    )}
                  {settled.tender === "cash" &&
                    settled.change_due_cents != null && (
                      <div className="pos-receipt-summary__row pos-receipt-summary__change">
                        <span>Change</span>
                        <span>{eur(settled.change_due_cents)}</span>
                      </div>
                    )}
                </div>
              </Space>
            }
            extra={
              <Button
                type="primary"
                size="large"
                onClick={() => setSettled(null)}
              >
                New sale
              </Button>
            }
          />
        </Card>
      </Shell>
    );
  }
  if (!station) {
    return (
      <Shell glow>
        <Card className="pos-pick-card" style={{ width: 380 }}>
          <div className="pos-brand">
            <PosBadge size={44} />
            <span className="pos-brand-word">Wanderzirkus</span>
          </div>
          <p className="pos-pick-hint">Choose a POS station to open the till.</p>
          <Space direction="vertical" style={{ width: "100%" }} size={10}>
            {stations.length === 0 && (
              <Empty description="No active POS stations. An Owner must create one." />
            )}
            {stations.map((s) => (
              <Button
                key={s.id}
                type="primary"
                block
                size="large"
                onClick={() => setStation(s)}
              >
                {s.name}
              </Button>
            ))}
          </Space>
        </Card>
      </Shell>
    );
  }

  // --- main two-pane sell layout ------------------------------------------
  const renderTile = (a: ArticleRow) => {
    const isRecipe = recipeArticleIds.has(a.id);
    return (
      <button
        key={a.id}
        type="button"
        className={`pos-article${isRecipe ? " pos-article--recipe" : ""}`}
        disabled={busy}
        onClick={() => void addArticle(a)}
      >
        <span className="pos-article-name">
          {a.name}
          {isRecipe && <span className="pos-article-recipe-tag">recipe</span>}
        </span>
        <span className="pos-article-price">{eur(posPrice(a))}</span>
      </button>
    );
  };

  return (
    <div className="pos-screen">
      {/* Catalog (left) */}
      <div className="pos-catalog">
        {/* No left top bar — the station controls live in the sidebar head.
            One scrolling layout grouped by category (sort order), each labelled
            by a slim 90°-rotated tab at its side, articles ordered by group. */}
        <div className="pos-catalog-body">
            <div
              className="pos-catalog-sections"
              ref={measureCatalog}
              style={{ "--cols": gridCols } as React.CSSProperties}
            >
              {catalogSections.map((sec) => (
                <section className="pos-cat-section" key={sec.catId ?? "other"}>
                  <div className="pos-cat-label">{sec.catName}</div>
                  <div className="pos-cat-block">
                    {sec.groups.map((g) => {
                      // Wider groups get more columns so they pack squarer (a
                      // 4-tile group → 2×2), but never more than actually fit —
                      // so a big group spreads across the row on a tablet
                      // instead of running vertically. `dense` backfills gaps.
                      const n = g.articles.length;
                      const ideal = n <= 2 ? 1 : n <= 6 ? 2 : n <= 12 ? 3 : 4;
                      const gc = Math.max(1, Math.min(ideal, gridCols));
                      return (
                        <div
                          className="pos-cat-group"
                          key={g.groupId ?? "ungrouped"}
                          style={
                            {
                              gridColumn: `span ${gc}`,
                              "--gc": gc,
                            } as React.CSSProperties
                          }
                        >
                          {g.groupName && (
                            <div className="pos-cat-group-label">{g.groupName}</div>
                          )}
                          <div className="pos-cat-group-grid">
                            {g.articles.map(renderTile)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
              {catalogSections.length === 0 && <Empty description="No articles" />}
            </div>
        </div>

        {/* Recent-orders ticker — settled orders this shift, newest on the right
            (nearest the till), older sliding left. Tap a pill to review an order
            when a customer asks about it. */}
        {(recent.length > 0 || pending != null) && (
          <div className="pos-recent" aria-label="Recent orders">
            {recent.map((o) => {
              const meta = o.tender ? TENDER_META[o.tender] : null;
              const Icon = meta?.Icon ?? Banknote;
              return (
                <button
                  key={o.id}
                  type="button"
                  className={`pos-recent__pill${
                    voided.has(o.id) ? " pos-recent__pill--voided" : ""
                  }`}
                  onClick={() => setRecentDetail(o)}
                  title={`${hhmm(o.captured_at)} · ${eur(o.total_cents)} · ${
                    meta?.label ?? "—"
                  }`}
                >
                  <span className="pos-recent__time">{hhmm(o.captured_at)}</span>
                  <span className="pos-recent__amt">{eur(o.total_cents)}</span>
                  <span className="pos-recent__pay">
                    <Icon size={14} />
                  </span>
                </button>
              );
            })}
            {/* The backgrounded card: blinks while waiting, turns green on paid.
                Tap it to manage (keep waiting / take cash / cancel). */}
            {pending && (
              <button
                type="button"
                className={`pos-recent__pill pos-pending pos-pending--${
                  pending.status === "paid" ? "paid" : "wait"
                }`}
                title={`Card · ${eur(pending.order.total_cents)} · ${pending.status}`}
                disabled={pending.status === "paid"}
                onClick={() => setCardBusyOpen(true)}
              >
                <span className="pos-recent__pay">
                  <CreditCard size={14} />
                </span>
                <span className="pos-recent__amt">
                  {eur(pending.order.total_cents)}
                </span>
                <span className="pos-recent__time">
                  {pending.status === "paid"
                    ? "paid"
                    : pending.readerDown
                      ? "tap again?"
                      : "paying…"}
                </span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Cart (right) */}
      <div className="pos-cart">
        {/* Static sidebar head: the crew logo (a hidden easter-egg button),
            barcode entry, fullscreen, and the cart display-mode toggle. There is
            no top bar on the catalog (left) anymore. */}
        <div className="pos-cart-head">
          <button
            type="button"
            className="pos-logo-btn"
            aria-label="Wanderzirkus"
            onClick={() => posSound.play("hidden")}
          >
            <PosBadge size={30} />
          </button>
          {/* Barcode/voucher entry: the physical scanner works hands-free
              (global listener); this opens a text input for manual entry. */}
          <Popover
            rootClassName="pos-surface"
            open={scanOpen}
            onOpenChange={(o) => {
              setScanOpen(o);
              if (!o) {
                setScan("");
                return;
              }
              // Lazy-load the customer-display URL the first time the popover
              // opens, so its QR can be scanned to open the display elsewhere.
              if (!displayUrl && station) {
                void api
                  .posDisplayToken(station.id)
                  .then((t) =>
                    setDisplayUrl(window.location.origin + t.path),
                  )
                  .catch(() => undefined);
              }
            }}
            trigger="click"
            placement="bottom"
            title="Enter a barcode or voucher"
            content={
              <Space direction="vertical" size={12} style={{ width: 280 }}>
                <Input
                  autoFocus
                  allowClear
                  placeholder="Barcode or voucher code…"
                  prefix={<ScanBarcode size={16} />}
                  value={scan}
                  onChange={(e) => setScan(e.target.value)}
                  onPressEnter={() => {
                    if (scan.trim()) onScan(scan);
                    setScanOpen(false);
                  }}
                  style={{ width: "100%" }}
                />
                {displayUrl && (
                  <div className="pos-scan-qr">
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: 11 }}
                    >
                      Scan to open the customer display on another screen
                    </Typography.Text>
                    <QRCode value={displayUrl} size={168} bordered={false} />
                  </div>
                )}
              </Space>
            }
          >
            <Button
              aria-label="Enter barcode or voucher"
              icon={<ScanBarcode size={18} />}
            />
          </Popover>
          <FullscreenButton type="default" />
          <div className="pos-cart-head__spacer" />
          {/* List rings every tap as its own line; Grouped merges repeats. */}
          <Segmented
            size="small"
            value={cartMode}
            onChange={(v) => changeCartMode(v as "list" | "aggregated")}
            options={[
              { value: "list", label: "List" },
              { value: "aggregated", label: "Grouped" },
            ]}
          />
        </div>
        <div className="pos-cart-lines" ref={cartLinesRef}>
          {(!order || order.lines.length === 0) && (
            <Empty description="Tap an article to start" />
          )}
          {/* LIST view — the order's individual lines in tap order (newest at
              the end); a bundled cup/Bottle is indented under its drink. Tap a
              sale line to reveal the red ✕ remove. */}
          {order &&
            cartMode === "list" &&
            order.lines.map((l) => {
              const cupId = cupConfig?.cup_article_id ?? null;
              const isCup = cupId != null && l.article_id === cupId;
              const removable = l.kind === "sale" && !isCup;
              const tappable = removable;
              const selected = selectedLineId === l.id;
              const bundled = l.parent_line_id != null;
              return (
                <div
                  key={l.id}
                  className={`pos-line${tappable ? " pos-line--tappable" : ""}${
                    selected ? " pos-line--selected" : ""
                  }${bundled ? " pos-line--bundled" : ""}`}
                  onClick={
                    tappable
                      ? () => setSelectedLineId(selected ? null : l.id)
                      : undefined
                  }
                >
                  <div className="pos-line-main">
                    <span className="pos-line-name">
                      {lineLabel(l, cupId, nameById)}
                      {l.qty > 1 && (
                        <span className="pos-line-qty"> ×{l.qty}</span>
                      )}
                    </span>
                    <span
                      className={`pos-line-amt${
                        lineSignedCents(l) < 0 ? " pos-line-amt--refund" : ""
                      }`}
                    >
                      {eur(lineSignedCents(l))}
                    </span>
                  </div>
                  {tappable && selected && (
                    <Button
                      danger
                      size="small"
                      disabled={busy}
                      aria-label="Remove from order"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedLineId(null);
                        void changeQty(l.id, 0);
                      }}
                    >
                      ✕ Remove
                    </Button>
                  )}
                </div>
              );
            })}

          {/* GROUPED view — the same flow aggregated by article (Cup/Bottle
              multiplied). Drinks get −/+ steppers (− removes the freshest one). */}
          {order &&
            cartMode === "aggregated" &&
            cartGroups.map((g) => (
              <div key={g.key} className="pos-line">
                <div className="pos-line-main">
                  <span className="pos-line-name">
                    {g.label}
                    {!g.steppable && g.qty > 1 && (
                      <span className="pos-line-qty"> ×{g.qty}</span>
                    )}
                  </span>
                  <span
                    className={`pos-line-amt${
                      g.refund ? " pos-line-amt--refund" : ""
                    }`}
                  >
                    {eur(g.cents)}
                  </span>
                </div>
                {g.steppable && (
                  <Space size={4}>
                    <Button
                      size="small"
                      disabled={busy}
                      onClick={() =>
                        void changeQty(g.freshestId, g.freshestQty - 1)
                      }
                    >
                      −
                    </Button>
                    <span className="pos-line-qty">{g.qty}</span>
                    <Button
                      size="small"
                      disabled={busy}
                      onClick={() =>
                        g.articleId != null &&
                        void addByArticle(g.articleId, g.priceCents)
                      }
                    >
                      +
                    </Button>
                  </Space>
                )}
              </div>
            ))}
        </div>

        <div className="pos-cart-foot">
          {/* Drink builders: an AD-HOC custom drink (raw ingredients + name +
              price → one stockless line) always available; the pre-defined
              servings bundler shown only when recipes exist. */}
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <Button
              block
              size="large"
              icon={<Wine size={18} />}
              disabled={busy}
              onClick={() => {
                setCustomName("");
                setCustomPrice(null);
                setCustomIngs([]);
                setCustomOpen(true);
              }}
            >
              Custom drink
            </Button>
            {recipeServings.length > 0 && (
              <Button
                block
                size="large"
                disabled={busy}
                onClick={() => {
                  setDrink([]);
                  setBuilderOpen(true);
                }}
              >
                Servings
              </Button>
            )}
          </div>
          {/* Cup / on-site-deposit quick-actions (out = charge, in = refund).
              One cup, one deposit → no picker. Disabled until configured. */}
          <div
            className="pos-quick-actions"
            style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}
          >
            <Button
              style={{ flex: 1 }}
              disabled={busy || !cupReady}
              onClick={() => void runQuickAction("cup", "out")}
            >
              Cup out
            </Button>
            <Button
              style={{ flex: 1 }}
              disabled={busy || !cupReady}
              onClick={() => void runQuickAction("cup", "in")}
            >
              Cup in
            </Button>
            {/* Deposit OUT is always bound to an article (its pfand rides along
                at sale), so only a standalone Deposit IN (a bottle returned for
                a refund) is offered here. Hidden entirely when bottle pfand is
                switched OFF for the venue — then Cup out/in fill the row. */}
            {depositEnabled && (
              <Button
                style={{ flex: 1 }}
                disabled={busy || !depositReady}
                onClick={() => void runQuickAction("deposit", "in")}
              >
                Bottle in
              </Button>
            )}
          </div>
          {(() => {
            // Only nudge to CONFIGURE what's enabled-but-unset; a deliberately
            // switched-off bottle pfand is silent.
            const needCup = !cupReady;
            const needDeposit = depositEnabled && !depositReady;
            if (!needCup && !needDeposit) return null;
            return (
              <Typography.Text
                type="secondary"
                style={{ fontSize: 11, display: "block", marginBottom: 8 }}
              >
                {needCup && needDeposit
                  ? "Set the cup + on-site deposit in POS → Cups & deposits to enable these."
                  : needCup
                    ? "Set the cup in POS → Cups & deposits to enable Cup out/in."
                    : "Set the on-site deposit in POS → Cups & deposits to enable Bottle in."}
              </Typography.Text>
            );
          })()}
          <div className="pos-total">
            <span>Total</span>
            <span>{eur(total)}</span>
          </div>

          <Space
            className="pos-settle-bar"
            style={{ width: "100%" }}
            direction="vertical"
            size={8}
          >
            {/* Tenders — each opens its own popup: cash → keypad, card → reader
                flow, staff / on-the-house → a confirmation with the total. Two
                rows: comps (shorter) over card/cash. Voucher is scan-driven. */}
            <div className="pos-tenders">
              <button
                type="button"
                className="pos-tender pos-tender--comp"
                disabled={!canPay || total < 0}
                onClick={() => openTender("staff_comp")}
              >
                Staff
              </button>
              <button
                type="button"
                className="pos-tender pos-tender--comp"
                disabled={!canPay || total < 0}
                onClick={() => openTender("client_comp")}
              >
                On the house
              </button>
              <button
                type="button"
                className="pos-tender pos-tender--card"
                disabled={!canPay || total < 0}
                onClick={() => openTender("card")}
              >
                Card
              </button>
              <button
                type="button"
                className="pos-tender pos-tender--cash"
                disabled={!canPay}
                onClick={() => openTender("cash")}
              >
                Cash
              </button>
            </div>
            <Space style={{ width: "100%" }}>
              <Button
                size="large"
                block
                disabled={!order || busy}
                onClick={() => void clearCart()}
              >
                Clear cart
              </Button>
              {/* Tip button — plays a sound and NOTHING else (no order effect). */}
              <Button
                size="large"
                block
                icon={<Bell size={18} />}
                onClick={() => posSound.play("tip")}
              >
                Tip
              </Button>
            </Space>
          </Space>
        </div>
      </div>

      {/* Tender popup — cash keypad, or a staff / on-the-house / voucher
          confirmation. Card has its own dual-rail overlay below. */}
      <Modal
        rootClassName="pos-surface"
        className={
          tenderModal === "cash" && total > 0 ? "pos-cash-modal" : undefined
        }
        open={tenderModal !== null && tenderModal !== "card"}
        footer={null}
        onCancel={() => {
          // Popup closed without settling → drop the display's settling hint.
          if (order) void api.posPresent(order.id, null).catch(() => undefined);
          setTenderModal(null);
          if (tenderModal === "cash") setTendered(null);
          if (tenderModal === "voucher") setArmedVoucher(null);
        }}
        title={
          tenderModal === "cash"
            ? "Cash"
            : tenderModal === "staff_comp"
              ? "Staff — on the house"
              : tenderModal === "client_comp"
                ? "On the house"
                : "Voucher"
        }
        width={tenderModal === "cash" ? (total <= 0 ? 380 : "94vw") : 420}
        destroyOnClose
      >
        {tenderModal === "cash" &&
          (total <= 0 ? (
            // Refund / deposit return — cash is paid OUT, so there's no amount
            // to receive: skip the keypad and confirm the pay-out directly.
            <div className="pos-tender-pop">
              <p className="pos-tender-pop__q">
                Pay this back out of the drawer (a deposit return / refund).
              </p>
              <div className="pos-tender-pop__total pos-tender-pop__total--big">
                <span>Pay out</span>
                <span>{eur(Math.abs(total))}</span>
              </div>
              <Button
                type="primary"
                size="large"
                block
                loading={busy}
                onClick={() => void doSettle("cash")}
              >
                Pay out {eur(Math.abs(total))}
              </Button>
            </div>
          ) : (
            // Two-column: the grouped order on the left (so the operator sees
            // what's paid), the keypad on the right.
            <div className="pos-tender-cols">
              <div className="pos-tender-items">
                <div className="pos-receipt-lines">
                  {orderGrouped.all.map((g, i) => (
                    <div key={i} className="pos-receipt-line">
                      <span>
                        {g.qty} × {g.label}
                      </span>
                      <span>{eur(g.cents)}</span>
                    </div>
                  ))}
                </div>
                <div className="pos-receipt-total">
                  <span>Total</span>
                  <span>{eur(total)}</span>
                </div>
                {change !== null && change >= 0 && (
                  <div className="pos-change">Change {eur(change)}</div>
                )}
              </div>
              <div className="pos-tender-pad">
                <div className="pos-tender-pad__total">
                  <span>Total</span>
                  <span>{eur(total)}</span>
                </div>
                <PosNumberPad
                  value={tendered ?? 0}
                  onChange={setTendered}
                  exactCents={total}
                />
                <div className="pos-change pos-change--big">
                  <span>Change</span>
                  <span>{eur(Math.max(0, change ?? 0))}</span>
                </div>
                <Button
                  type="primary"
                  size="large"
                  block
                  loading={busy}
                  disabled={!cashCovers}
                  onClick={() => void doSettle("cash")}
                >
                  Settle {eur(total)}
                </Button>
              </div>
            </div>
          ))}
        {(tenderModal === "staff_comp" || tenderModal === "client_comp") && (
          <div className="pos-tender-pop">
            <p className="pos-tender-pop__q">
              {tenderModal === "staff_comp"
                ? "On the house for staff — the drinks below are free (no deposit)."
                : "On the house — the drinks below are free (no deposit)."}
            </p>
            <div className="pos-receipt-lines">
              {orderGrouped.drinks.map((g, i) => (
                <div key={i} className="pos-receipt-line">
                  <span>
                    {g.qty} × {g.label}
                  </span>
                  <span>{eur(g.cents)}</span>
                </div>
              ))}
              {orderGrouped.drinks.length === 0 && (
                <Typography.Text type="secondary">
                  No drinks to comp.
                </Typography.Text>
              )}
            </div>
            <Button
              type="primary"
              size="large"
              block
              loading={busy}
              disabled={orderGrouped.drinks.length === 0}
              onClick={() => void doSettle(tenderModal)}
            >
              On the house
            </Button>
          </div>
        )}
        {tenderModal === "voucher" && (
          <div className="pos-tender-pop">
            {armedVoucher && (
              <div className="pos-tender-pop__total">
                <span>Voucher {armedVoucher.code}</span>
                <span>
                  {armedVoucher.available == null
                    ? "—"
                    : eur(armedVoucher.available)}
                </span>
              </div>
            )}
            <div className="pos-tender-pop__total pos-tender-pop__total--big">
              <span>Voucher pays</span>
              <span>{eur(voucherTotal)}</span>
            </div>
            {depositCents !== 0 && (
              <Typography.Text type="secondary">
                Deposit {eur(depositCents)} dropped — vouchers carry no deposit.
              </Typography.Text>
            )}
            {voucherCovers ? (
              <Typography.Text type="success">
                Covers the order — charged to the voucher holder.
              </Typography.Text>
            ) : (
              <Typography.Text type="danger">
                Does not cover {eur(voucherTotal)} — take the remainder on cash or
                card.
              </Typography.Text>
            )}
            <Button
              type="primary"
              size="large"
              block
              loading={busy}
              disabled={!voucherCovers}
              onClick={() => void doSettle("voucher")}
            >
              Pay with voucher · {eur(voucherTotal)}
            </Button>
          </div>
        )}
      </Modal>

      {/* Manage / failure popup for a card. BACKGROUND mode: dismissible while
          waiting (keep waiting / cash / cancel). FOREGROUND mode (backgrounding
          off): non-dismissible while waiting — it blocks until paid / declined /
          cash / cancel. On decline/expire/error it's always LOCKED. */}
      <Modal
        rootClassName="pos-surface"
        open={pending != null && (cardFailed || cardBusyOpen)}
        footer={null}
        closable={!cardFailed && (cardBackgroundEnabled || pending?.status === "paid")}
        maskClosable={
          !cardFailed && (cardBackgroundEnabled || pending?.status === "paid")
        }
        keyboard={!cardFailed && cardBackgroundEnabled}
        onCancel={() => {
          setCardBusyOpen(false);
          setFallbackCash(false);
        }}
        title={
          pending?.status === "paid"
            ? "Payment received"
            : cardFailed
              ? "Card payment failed"
              : "Card is paying"
        }
        width={720}
      >
        {pending &&
          (() => {
            const cupId = cupConfig?.cup_article_id ?? null;
            const groups = groupOrderLines(pending.order.lines, cupId, nameById);
            const oTotal = pending.order.total_cents;
            const fbChange =
              fallbackTendered !== null ? fallbackTendered - oTotal : null;
            const fbCovers =
              oTotal <= 0 ||
              (fallbackTendered !== null && fallbackTendered >= oTotal);
            return (
              <div className="pos-tender-cols">
                <div className="pos-tender-items">
                  <div className="pos-receipt-lines">
                    {groups.map((g, i) => (
                      <div key={i} className="pos-receipt-line">
                        <span>
                          {g.qty} × {g.label}
                        </span>
                        <span>{eur(g.cents)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="pos-receipt-total">
                    <span>Total</span>
                    <span>{eur(oTotal)}</span>
                  </div>
                </div>
                {pending.status === "paid" ? (
                  <div className="pos-card-fail">
                    <CreditCard size={40} className="pos-card-fail__icon" />
                    <p className="pos-card-fail__msg">
                      Card payment received — {eur(oTotal)}.
                      {pending.receiptNo ? ` Receipt ${pending.receiptNo}.` : ""}
                    </p>
                    <Button
                      type="primary"
                      size="large"
                      block
                      onClick={() => {
                        setCardBusyOpen(false);
                        setPending(null);
                      }}
                    >
                      Done
                    </Button>
                  </div>
                ) : !fallbackCash ? (
                  <div className="pos-card-fail">
                    <CreditCard size={40} className="pos-card-fail__icon" />
                    <p className="pos-card-fail__msg">
                      {cardFailed ? (
                        <>
                          The card{" "}
                          {pending.status === "expired"
                            ? "payment expired"
                            : pending.status === "error"
                              ? "couldn't start"
                              : "was declined"}
                          . Settle this order in cash or cancel it — it won't
                          return to the till.
                        </>
                      ) : pending.readerDown ? (
                        "The reader stopped waiting (its ~60-second tap window passed). Re-present the amount to the reader, or have the customer scan the QR on the customer screen — or take cash / cancel."
                      ) : cardBackgroundEnabled ? (
                        "The card is paying — tap the reader (or scan on the customer screen). Keep waiting, take cash instead, or cancel."
                      ) : (
                        "The card is paying — tap the reader (or scan on the customer screen). This will settle automatically; or take cash instead / cancel."
                      )}
                    </p>
                    {!cardFailed && pending.readerDown && (
                      <Button
                        type="primary"
                        size="large"
                        block
                        loading={busy}
                        onClick={() => void rearmReader()}
                      >
                        Re-present to reader
                      </Button>
                    )}
                    <Button
                      type={
                        !cardFailed && pending.readerDown ? "default" : "primary"
                      }
                      size="large"
                      block
                      onClick={() => {
                        setFallbackCash(true);
                        setFallbackTendered(null);
                      }}
                    >
                      Take cash instead
                    </Button>
                    <Button
                      danger
                      size="large"
                      block
                      loading={busy}
                      onClick={() => void cancelPending()}
                    >
                      Cancel order
                    </Button>
                    {!cardFailed && cardBackgroundEnabled && (
                      <Button
                        size="large"
                        block
                        onClick={() => setCardBusyOpen(false)}
                      >
                        Keep waiting
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="pos-tender-pad">
                    <div className="pos-tender-pad__total">
                      <span>Total</span>
                      <span>{eur(oTotal)}</span>
                    </div>
                    <PosNumberPad
                      value={fallbackTendered ?? 0}
                      onChange={setFallbackTendered}
                      exactCents={oTotal}
                    />
                    <div className="pos-change pos-change--big">
                      <span>Change</span>
                      <span>{eur(Math.max(0, fbChange ?? 0))}</span>
                    </div>
                    <Button
                      type="primary"
                      size="large"
                      block
                      loading={busy}
                      disabled={!fbCovers}
                      onClick={() => void settleFallbackCash()}
                    >
                      Settle {eur(oTotal)}
                    </Button>
                  </div>
                )}
              </div>
            );
          })()}
      </Modal>

      {/* Custom-drink builder: tap servings to assemble a drink, see the live
          running total, then add every serving to the order at once. Each
          serving is a recipe article that fans out its ingredients at settle. */}
      <Modal
        rootClassName="pos-surface"
        open={builderOpen}
        title="Build a custom drink"
        okText={
          drink.length === 0
            ? "Add to order"
            : `Add ${drink.length} to order · ${eur(drinkTotal)}`
        }
        okButtonProps={{ disabled: drink.length === 0 || busy }}
        onOk={() => void commitDrink()}
        onCancel={() => {
          setBuilderOpen(false);
          setDrink([]);
        }}
        width={680}
      >
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <div className="pos-builder-grid">
            {recipeServings.map((s) => {
              const out = s.available != null && s.available <= 0;
              return (
                <button
                  key={s.recipeId}
                  type="button"
                  className="pos-article"
                  disabled={out}
                  onClick={() =>
                    setDrink((d) => [
                      ...d,
                      { articleId: s.articleId, name: s.name, price: s.price },
                    ])
                  }
                >
                  <span className="pos-article-name">{s.name}</span>
                  <span className="pos-article-price">
                    {eur(s.price)}
                    {s.available != null && (
                      <Typography.Text
                        type={out ? "danger" : "secondary"}
                        style={{ fontSize: 11, display: "block" }}
                      >
                        {out ? "out of stock" : `${s.available} left`}
                      </Typography.Text>
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          {drink.length === 0 ? (
            <Empty description="Tap servings to build a drink" />
          ) : (
            <Card size="small" title={`Drink · ${eur(drinkTotal)}`}>
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                {drink.map((s, i) => (
                  <div
                    key={i}
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <span>{s.name}</span>
                    <Space>
                      <span>{eur(s.price)}</span>
                      <Button
                        size="small"
                        onClick={() =>
                          setDrink((d) => d.filter((_, j) => j !== i))
                        }
                      >
                        −
                      </Button>
                    </Space>
                  </div>
                ))}
              </Space>
            </Card>
          )}
        </Space>
      </Modal>

      {/* AD-HOC custom drink (add-pos-adhoc-custom-drink): name it, tap raw
          ingredients + dial amounts, set a price → one stockless line that fans
          out its ingredients at settle. Touch-only (big tap targets). */}
      <Modal
        rootClassName="pos-surface"
        open={customOpen}
        title="Custom drink"
        width={780}
        okText={
          customIngs.length === 0 || customPrice == null
            ? "Add to order"
            : `Add to order · ${eur(customPrice)}`
        }
        okButtonProps={{
          disabled: customIngs.length === 0 || customPrice == null || busy,
          size: "large",
        }}
        cancelButtonProps={{ size: "large" }}
        onOk={() => void commitCustomDrink()}
        onCancel={() => setCustomOpen(false)}
      >
        <div className="pos-custom">
          <Input
            size="large"
            placeholder="Drink name (e.g. Gin & Tonic)"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            maxLength={80}
            allowClear
          />
          <div className="pos-custom__cols">
            <div className="pos-builder-grid pos-custom__pick">
              {consumables.length === 0 && (
                <Empty description="No consumable articles" />
              )}
              {consumables.map((c) => {
                const picked = customIngs.some((i) => i.rootId === c.rootId);
                return (
                  <button
                    key={c.rootId}
                    type="button"
                    className={`pos-article${picked ? " pos-article--recipe" : ""}`}
                    onClick={() =>
                      setCustomIngs((xs) =>
                        xs.some((i) => i.rootId === c.rootId)
                          ? xs
                          : [
                              ...xs,
                              {
                                rootId: c.rootId,
                                name: c.name,
                                unit: c.unit,
                                amount: ingDefault(c.unit),
                              },
                            ],
                      )
                    }
                  >
                    <span className="pos-article-name">{c.name}</span>
                    <span className="pos-article-price">{ingUnitWord(c.unit)}</span>
                  </button>
                );
              })}
            </div>
            <div className="pos-custom__side">
              {customIngs.length === 0 ? (
                <Empty description="Tap ingredients to build the drink" />
              ) : (
                <div className="pos-custom__list">
                  {customIngs.map((i, idx) => (
                    <div key={i.rootId} className="pos-custom__ing">
                      <span className="pos-custom__ing-name">{i.name}</span>
                      <div className="pos-custom__ing-amt">
                        <Button
                          size="large"
                          onClick={() => bumpIng(idx, -ingStep(i.unit))}
                        >
                          −
                        </Button>
                        <span className="pos-custom__ing-qty">
                          {i.amount} {ingUnitLabel(i.unit)}
                        </span>
                        <Button
                          size="large"
                          onClick={() => bumpIng(idx, ingStep(i.unit))}
                        >
                          +
                        </Button>
                        <Button
                          size="large"
                          danger
                          onClick={() =>
                            setCustomIngs((xs) => xs.filter((_, j) => j !== idx))
                          }
                        >
                          ×
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="pos-custom__price">
                <span className="pos-custom__price-label">Price</span>
                <PosNumberPad
                  value={customPrice ?? 0}
                  onChange={setCustomPrice}
                  showPresets={false}
                />
              </div>
            </div>
          </div>
        </div>
      </Modal>

      {/* Recent-order review — opened from a ticker pill to answer a customer
          question (amounts, payment method, change, SumUp/card outcome). */}
      <Modal
        rootClassName="pos-surface"
        open={recentDetail !== null}
        footer={null}
        onCancel={() => setRecentDetail(null)}
        title={
          recentDetail
            ? `Order ${recentDetail.receipt_no ?? `#${recentDetail.id}`}`
            : "Order"
        }
        width={400}
        destroyOnClose
      >
        {recentDetail &&
          (() => {
            const meta = recentDetail.tender
              ? TENDER_META[recentDetail.tender]
              : null;
            const cupId = cupConfig?.cup_article_id ?? null;
            const groups = groupOrderLines(recentDetail.lines, cupId, nameById);
            return (
              <div className="pos-review">
                <div className="pos-review__meta">
                  <span>{hhmm(recentDetail.captured_at)}</span>
                  <span className="pos-review__pay">
                    {meta && <meta.Icon size={15} />}
                    {meta?.label ?? "—"}
                  </span>
                </div>
                <div className="pos-receipt-lines">
                  {groups.map((g, i) => (
                    <div key={i} className="pos-receipt-line">
                      <span>
                        {g.qty} × {g.label}
                      </span>
                      <span>{eur(g.cents)}</span>
                    </div>
                  ))}
                </div>
                <div className="pos-receipt-total">
                  <span>Total</span>
                  <span>{eur(recentDetail.total_cents)}</span>
                </div>
                {recentDetail.tender === "cash" &&
                  recentDetail.tendered_cents != null && (
                    <>
                      <div className="pos-receipt-line">
                        <span>Received</span>
                        <span>{eur(recentDetail.tendered_cents)}</span>
                      </div>
                      {recentDetail.change_due_cents != null && (
                        <div className="pos-receipt-line">
                          <span>Change</span>
                          <span>{eur(recentDetail.change_due_cents)}</span>
                        </div>
                      )}
                    </>
                  )}
                {recentDetail.tender === "card" && (
                  <div className="pos-receipt-line">
                    <span>Card</span>
                    <span className="pos-review__ok">Paid</span>
                  </div>
                )}
                <div className="pos-review__actions">
                  {voided.has(recentDetail.id) ? (
                    <Typography.Text type="danger" strong>
                      Reversed
                    </Typography.Text>
                  ) : (
                    <Button
                      danger
                      size="large"
                      block
                      loading={busy}
                      onClick={() => void stornoOrder(recentDetail)}
                    >
                      Storno —{" "}
                      {recentDetail.tender === "card"
                        ? "refund to card"
                        : "give money back"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })()}
      </Modal>
    </div>
  );
};

/** Centered full-viewport wrapper for the non-sell states (load/station/receipt).
 *  `glow` paints the coral+gold poster radial glow behind brand moments. */
const Shell = ({
  children,
  glow = false,
}: {
  children: React.ReactNode;
  glow?: boolean;
}) => (
  <div className={`pos-shell${glow ? " pos-shell--glow" : ""}`}>{children}</div>
);
